import type { ReactNode } from "react";
import { Info } from "lucide-react";
import type { OrgStoryView, StorySectionKey } from "@/lib/orgStory";
import SurfaceCard from "@/components/primitives/SurfaceCard";
import { SectionHead, TagRow } from "@/components/organization/kit";
import OfficesCard, { groupOfficesByState } from "@/components/organization/OfficesCard";
import { officesDisplayFor } from "@/lib/workModel";
import MissionStatement from "@/components/organization/overview/MissionStatement";
import BrandStory from "@/components/organization/overview/BrandStory";
import QuickStatsGrid from "@/components/organization/overview/QuickStatsGrid";
import VerifiedAgentCountLabel from "@/components/organization/overview/VerifiedAgentCountLabel";
import VideoPlayer from "@/components/organization/overview/VideoPlayer";
import PhotoGallery from "@/components/organization/overview/PhotoGallery";
import ProfileDataValueProp, {
  valuePartsWithContent,
  type ValuePart,
} from "@/components/organization/value/ProfileDataValueProp";
import ProductionMetrics from "@/components/organization/performance/ProductionMetrics";
import AgentPerformance from "@/components/organization/performance/AgentPerformance";
import MarketRankings from "@/components/organization/performance/MarketRankings";
import GrowthTrajectory from "@/components/organization/performance/GrowthTrajectory";
import MarketPresenceCard from "@/components/organization/performance/MarketPresence";
import StorySection from "./StorySection";
import { buildPerformance, buildQuickStats, type StoryPerformance } from "./storyData";
import OperationsSpecSheet from "./operations/OperationsSpecSheet";
import { EMPTY_TOOL_STACK, type NormalizedStack } from "@/lib/toolStack";
import { normalizeSupport } from "@/lib/orgSupport";

/**
 * The story's data sections (spec 2026-09-24 §5.1), each built from the
 * existing organization components. Which of them render is decided by
 * `view.sections`; a section here may still omit an individual card that has
 * no content, exactly as the tabs did.
 *
 * `storySectionHasContent` is the shared presence predicate: the same rules
 * that decide whether a section's content would actually render also decide
 * whether the section counts as "present" for anything upstream (e.g. a page
 * that only wants to list occupied sections). Each exported *Section
 * component calls the identical per-key helper and returns null itself when
 * it is false, so the two can never drift into disagreeing about whether a
 * section has content — no empty header, eyebrow or card shell for a section
 * whose content all filtered out to nothing.
 */

type Pd = Record<string, any>;
const text = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";

// The Growth section (spec 2026-09-24 §5.1) covers onboarding, training and
// career-advancement content — GROWTH_PARTS pulls all three from
// ProfileDataValueProp's shared value-prop parts, anchored by the full
// onboarding plan (`#onboarding-plan`, see OnboardingPlan.tsx's
// ONBOARDING_PLAN_ANCHOR_ID).
const OFFER_PARTS: readonly ValuePart[] = ["comp", "fees", "leads", "benefits"];
const GROWTH_PARTS: readonly ValuePart[] = ["onboarding", "training", "growth"];
// The signed-out page hides the compensation model, fees and leads behind a
// sign-up prompt (founder decision F6).
const GATED_OFFER_PARTS: readonly ValuePart[] = ["comp", "fees", "leads"];
const PUBLIC_OFFER_PARTS: readonly ValuePart[] = OFFER_PARTS.filter(
  (part) => !GATED_OFFER_PARTS.includes(part),
);

interface OverviewModel {
  pd: Pd;
  quote: Pd;
  paragraphs: string[];
  hasQuote: boolean;
  photos: any[];
  stats: ReturnType<typeof buildQuickStats>;
}

/** The Overview section's derived data, shared by the predicate and the render so neither can drift from the other. */
function overviewModel(view: OrgStoryView): OverviewModel {
  const pd = view.profileData as Pd;
  const quote: Pd = pd.featuredQuote ?? {};
  const paragraphs = [pd.ourStory, pd.whatMakesUsDifferent].filter(text);
  const hasQuote = text(quote.text);
  const photos = Array.isArray(pd.galleryPhotos) ? pd.galleryPhotos : [];
  const stats = buildQuickStats(view);
  return { pd, quote, paragraphs, hasQuote, photos, stats };
}

function overviewHasContent(view: OrgStoryView): boolean {
  const { pd, paragraphs, hasQuote, stats, photos } = overviewModel(view);
  return (
    text(pd.missionStatement) ||
    paragraphs.length > 0 ||
    hasQuote ||
    stats.length > 0 ||
    text(pd.brandVideoUrl) ||
    photos.length > 0
  );
}

function offerHasContent(pd: Pd, onBookCall?: () => void): boolean {
  return valuePartsWithContent(pd, { parts: OFFER_PARTS, onBookCall }).length > 0;
}

function growthHasContent(pd: Pd): boolean {
  return valuePartsWithContent(pd, { parts: GROWTH_PARTS }).length > 0;
}

function operationsHasContent(pd: Pd, stack: NormalizedStack): boolean {
  return !stack.isEmpty || !normalizeSupport(pd).isEmpty;
}

/** A perf object can be non-null yet carry no non-empty block — e.g. an
 * overall gate satisfied by a malformed raw rankings/timeline entry while
 * every filtered block ends up empty. Treat that as absent: no disclaimer,
 * no footer, no shell. */
function performanceHasContent(perf: StoryPerformance | null): boolean {
  return !!perf && !!(perf.production || perf.agents || perf.rankings.length > 0 || perf.growth.length > 0 || perf.presence);
}

/** Footprint's specialties list, shared by the predicate and the render. */
function footprintSpecialties(pd: Pd): string[] {
  return (Array.isArray(pd.specialties) ? pd.specialties : []).filter(text);
}

function footprintHasContent(view: OrgStoryView): boolean {
  const pd = view.profileData as Pd;
  const specialties = footprintSpecialties(pd);
  const hasFocus = view.serviceTypes.length > 0 || specialties.length > 0;
  // Mirrors OfficesCard's own null condition — an office list that groups to
  // nothing (e.g. every entry blank) must not count as footprint content.
  const hasOffices = groupOfficesByState(view.officeLabels).length > 0;
  const hasPerf = performanceHasContent(buildPerformance(view));
  return hasFocus || hasOffices || hasPerf;
}

/**
 * Whether the named section currently has content to show, using the exact
 * same predicates the corresponding *Section component renders with. `team`,
 * `reviews` and `news` self-report through their own data fetch (`onResolved`
 * elsewhere) rather than from `view`, so they are always reported present
 * here — this function only arbitrates the five data-driven sections built in
 * this file.
 */
export function storySectionHasContent(
  key: StorySectionKey,
  view: OrgStoryView,
  ctx: { stack?: NormalizedStack; onBookCall?: () => void } = {},
): boolean {
  const pd = view.profileData as Pd;
  switch (key) {
    case "overview":
      return overviewHasContent(view);
    case "offer":
      return offerHasContent(pd, ctx.onBookCall);
    case "growth":
      return growthHasContent(pd);
    case "operations":
      return operationsHasContent(pd, ctx.stack ?? EMPTY_TOOL_STACK);
    case "footprint":
      return footprintHasContent(view);
    case "team":
    case "reviews":
    case "news":
      return true;
    default:
      return true;
  }
}

export function OverviewSection({ view, isPublished }: { view: OrgStoryView; isPublished: boolean }) {
  if (!overviewHasContent(view)) return null;

  const { pd, quote, paragraphs, hasQuote, photos, stats: rawStats } = overviewModel(view);
  const stats = rawStats.map((s) => ({
    label: s.verified ? <VerifiedAgentCountLabel /> : s.label,
    value: s.value,
    icon: s.icon,
    subtext: s.subtext,
  }));

  return (
    <StorySection section="overview">
      {text(pd.missionStatement) && <MissionStatement mission={pd.missionStatement} />}
      {(paragraphs.length > 0 || hasQuote) && (
        <BrandStory
          paragraphs={paragraphs}
          pullQuote={hasQuote ? quote.text : ""}
          pullQuoteAuthor={hasQuote ? quote.attribution : ""}
        />
      )}
      {stats.length > 0 && <QuickStatsGrid stats={stats} />}
      {text(pd.brandVideoUrl) && (
        <VideoPlayer
          thumbnailUrl={pd.coverImage || view.identity.coverUrl || ""}
          title={`Welcome to ${view.identity.name}`}
          videoUrl={pd.brandVideoUrl}
          // Signed-in only: an anonymous engagement insert is refused by RLS.
          orgId={view.audience === "signedIn" ? view.identity.orgId : undefined}
          isPublished={isPublished}
        />
      )}
      {photos.length > 0 && <PhotoGallery photos={photos} />}
    </StorySection>
  );
}

export function OfferSection({
  view,
  onBookCall,
  insightsTeaser,
}: {
  view: OrgStoryView;
  onBookCall?: () => void;
  /**
   * Signed-out page only (founder decision F6): stands in for the compensation
   * model, fee and lead blocks, which are then not rendered. Shown only when
   * the organization has any of them to hide.
   */
  insightsTeaser?: ReactNode;
}) {
  const pd = view.profileData as Pd;
  if (!offerHasContent(pd, onBookCall)) return null;

  if (insightsTeaser) {
    const hasGated = valuePartsWithContent(pd, { parts: GATED_OFFER_PARTS }).length > 0;
    return (
      <StorySection section="offer">
        {hasGated && insightsTeaser}
        <ProfileDataValueProp profileData={pd} orgName={view.identity.name} parts={PUBLIC_OFFER_PARTS} />
      </StorySection>
    );
  }

  return (
    <StorySection section="offer">
      <ProfileDataValueProp profileData={pd} orgName={view.identity.name} parts={OFFER_PARTS} onBookCall={onBookCall} />
    </StorySection>
  );
}

export function GrowthSection({ view }: { view: OrgStoryView }) {
  const pd = view.profileData as Pd;
  if (!growthHasContent(pd)) return null;

  return (
    <StorySection section="growth">
      <ProfileDataValueProp profileData={pd} orgName={view.identity.name} parts={GROWTH_PARTS} />
    </StorySection>
  );
}

export function OperationsSection({ view, stack }: { view: OrgStoryView; stack: NormalizedStack }) {
  const pd = view.profileData as Pd;
  if (!operationsHasContent(pd, stack)) return null;

  return (
    <StorySection section="operations">
      <OperationsSpecSheet stack={stack} support={normalizeSupport(pd)} />
    </StorySection>
  );
}

export function FootprintSection({ view }: { view: OrgStoryView }) {
  if (!footprintHasContent(view)) return null;

  const pd = view.profileData as Pd;
  const specialties = footprintSpecialties(pd);
  const perf = buildPerformance(view);
  const showPerf = performanceHasContent(perf);

  return (
    <StorySection section="footprint">
      {(view.serviceTypes.length > 0 || specialties.length > 0) && (
        <SurfaceCard padding="lg" className="rounded-2xl">
          <SectionHead kicker="Culture" title="What it's like to work here" />
          {view.serviceTypes.length > 0 && <TagRow tags={view.serviceTypes} />}
          {specialties.length > 0 && (
            <div className={view.serviceTypes.length > 0 ? "mt-4" : undefined}>
              <div className="eyebrow mb-3 text-ink-500">Specialties</div>
              <TagRow tags={specialties} />
            </div>
          )}
        </SurfaceCard>
      )}

      <OfficesCard
        offices={view.officeLabels}
        display={officesDisplayFor(view.workModel, pd.officesDisplay)}
        cloud={view.workModel.treatedAsCloud}
      />

      {showPerf && perf && (
        <>
          <div className="flex items-start gap-3 rounded-[var(--radius-md)] border border-border bg-secondary px-4 py-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <p className="text-sm text-ink-700">
              These metrics are self-reported by the organization. Compaire will display a{" "}
              <span className="font-semibold">Verified</span> badge on metrics confirmed by MLS or
              third-party data partners.
            </p>
          </div>
          {perf.production && <ProductionMetrics {...perf.production} />}
          {perf.agents && <AgentPerformance {...perf.agents} />}
          {perf.rankings.length > 0 && <MarketRankings rankings={perf.rankings} />}
          {perf.growth.length > 0 && (
            <GrowthTrajectory milestones={perf.growth} narrative="Year over year organizational growth" />
          )}
          {perf.presence && <MarketPresenceCard {...perf.presence} />}
          <p className="pt-4 text-center text-xs text-ink-500">
            Data reflects fiscal year {perf.reportingYear}, self-reported by the organization.
          </p>
        </>
      )}
    </StorySection>
  );
}
